import { getAuth, onAuthStateChanged, User } from "@firebase/auth";
import * as Sentry from "@sentry/react";

// Constants
const AUTH_INIT_TIMEOUT_MS = 10000; // 10 seconds max wait for auth initialization
const TOKEN_FETCH_TIMEOUT_MS = 10000; // 10 seconds max wait for token fetch

// Track if auth has been initialized
let authInitialized = false;
let authInitPromise: Promise<User | null> | null = null;

/**
 * Wait for Firebase Auth to initialize and return the current user.
 * Firebase auth state is async on page load - this ensures we wait for it.
 * @param timeoutMs Maximum time to wait (default: 10 seconds)
 * @returns Promise resolving to User or null
 */
export const waitForAuthInit = (timeoutMs: number = AUTH_INIT_TIMEOUT_MS): Promise<User | null> => {
  // If already initialized, return current user immediately
  if (authInitialized) {
    return Promise.resolve(getAuth().currentUser);
  }

  // If initialization is in progress, return the existing promise
  if (authInitPromise) {
    return authInitPromise;
  }

  // Start initialization
  authInitPromise = new Promise((resolve) => {
    const auth = getAuth();
    
    // Set up timeout
    const timeoutId = setTimeout(() => {
      Sentry.captureMessage("Firebase auth initialization timeout", {
        level: "warning",
        tags: { auth_flow: "auth_init_timeout" },
      });
      authInitialized = true;
      resolve(null);
    }, timeoutMs);

    // Listen for auth state
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      clearTimeout(timeoutId);
      unsubscribe();
      authInitialized = true;
      authInitPromise = null;
      resolve(user);
    });
  });

  return authInitPromise;
};

/**
 * Get role from Firebase token claims.
 * Useful when localStorage role is missing but Firebase session exists.
 * 
 * @returns Promise resolving to role string or empty string
 */
export const getRoleFromFirebaseToken = async (): Promise<string> => {
  try {
    const user = await waitForAuthInit();
    if (!user) return "";

    const tokenResult = await user.getIdTokenResult();
    
    if (
      tokenResult.claims &&
      typeof tokenResult.claims === "object" &&
      "roles" in tokenResult.claims &&
      tokenResult.claims.roles &&
      typeof tokenResult.claims.roles === "object" &&
      "data" in tokenResult.claims.roles &&
      typeof tokenResult.claims.roles.data === "string"
    ) {
      return tokenResult.claims.roles.data;
    }
    
    return "";
  } catch (error) {
    Sentry.captureException(error, {
      level: "error",
      tags: { auth_flow: "get_role_from_token" },
    });
    return "";
  }
};

/**
 * Get the current Firebase ID token.
 * Waits for auth initialization if needed, then fetches token.
 * Firebase SDK automatically refreshes expired tokens.
 * Also refreshes role in session when token is force refreshed.
 * 
 * @param forceRefresh Force a token refresh even if not expired
 * @param timeoutMs Maximum time to wait for token (default: 10 seconds)
 * @returns Promise resolving to token string or null
 */
export const getFirebaseIdToken = async (
  forceRefresh: boolean = false,
  timeoutMs: number = TOKEN_FETCH_TIMEOUT_MS
): Promise<string | null> => {
  try {
    // Wait for auth to initialize first
    const user = await waitForAuthInit(timeoutMs);
    
    if (!user) {
      return null;
    }

    // Get token with timeout
    const tokenPromise = user.getIdToken(forceRefresh);
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => {
        Sentry.captureMessage("Firebase token fetch timeout", {
          level: "warning",
          tags: { auth_flow: "token_fetch_timeout" },
        });
        resolve(null);
      }, timeoutMs);
    });

    const token = await Promise.race([tokenPromise, timeoutPromise]);
    
    // If we got a token and force refreshed, also refresh the role in session
    if (token && forceRefresh) {
      try {
        const role = await getRoleFromFirebaseToken();
        if (role) {
          // Import dynamically to avoid circular dependency
          const { setSession } = await import("./session");
          setSession("role", role);
        }
      } catch (roleError) {
        // Don't fail token fetch if role refresh fails
        Sentry.captureException(roleError, {
          level: "warning",
          tags: { auth_flow: "role_refresh_on_token" },
        });
      }
    }
    
    return token;
  } catch (error) {
    Sentry.captureException(error, {
      level: "error",
      tags: { auth_flow: "get_firebase_token" },
    });
    return null;
  }
};

/**
 * Get the current Firebase user's UID.
 * Waits for auth initialization if needed.
 * 
 * @returns Promise resolving to user ID or null
 */
export const getFirebaseUserId = async (): Promise<string | null> => {
  try {
    const user = await waitForAuthInit();
    return user?.uid || null;
  } catch (error) {
    Sentry.captureException(error, {
      level: "error",
      tags: { auth_flow: "get_firebase_user_id" },
    });
    return null;
  }
};

/**
 * Check if Firebase user is currently authenticated.
 * Does NOT wait for auth initialization - returns current state.
 * 
 * @returns boolean indicating if user is authenticated
 */
export const isFirebaseAuthenticated = (): boolean => {
  const auth = getAuth();
  return !!auth.currentUser;
};

/**
 * Get current Firebase user synchronously.
 * Returns null if auth not initialized or no user.
 * 
 * @returns User or null
 */
export const getCurrentFirebaseUser = (): User | null => {
  return getAuth().currentUser;
};

/**
 * Reset auth initialization state.
 * Call this after logout to ensure fresh state on next login.
 */
export const resetAuthState = (): void => {
  authInitialized = false;
  authInitPromise = null;
};

/**
 * Check if user has a valid Firebase auth session.
 * This is a synchronous check that returns true if Firebase has a current user.
 * Use this instead of getSession("customToken") for auth state checks.
 * 
 * @returns boolean indicating if user has a valid auth session
 */
export const hasValidAuthSession = (): boolean => {
  const auth = getAuth();
  return !!auth.currentUser;
};

/**
 * Get the current Firebase auth token synchronously if available.
 * Returns null if no user is authenticated or if auth is not initialized.
 * For async token retrieval with refresh capability, use getFirebaseIdToken instead.
 * 
 * Note: This is primarily for backward compatibility. Prefer getFirebaseIdToken
 * for API calls as it handles token refresh automatically.
 * 
 * @returns Promise resolving to token string or null
 */
export const getAuthToken = async (): Promise<string | null> => {
  return getFirebaseIdToken(false);
};
